import { DATA_SOURCES, INSTAGRAM_COUNTRIES } from '@/config/constants';
import { useCurrentCountry, useInstagramData } from 'utils/hooks';
import { useEntity } from 'utils/hooks/api';
import useEntityBase, { isEmptyRow } from './useEntityBase';
import useInstagramWeeks from 'utils/hooks/useInstagramWeeks';

/** One Instagram entity - a brand or a category - with its week's row. */
const useInstagramEntity = ({ entityType, entityId, controller, coverage }) => {
    const entityQuery = useEntity({
        id: entityId,
        entityType,
        dataSource: DATA_SOURCES.INSTAGRAM,
    });
    const entity = entityQuery.data;
    const country = useCurrentCountry();
    const { week, year, query: weeksQuery } = useInstagramWeeks();

    const rowQuery = useInstagramData({
        paginateBy: entityType,
        enabled: !!(country && entityId),
        [entityType]: entityId ? [entityId] : null,
        countries: country ? [country.id] : null,
    });
    const row = rowQuery.data?.[0];
    const details = entity || row || null;

    return useEntityBase({
        dataSource: DATA_SOURCES.INSTAGRAM,
        slug: 'instagram',
        entityType,
        entityId,
        controller,
        entity,
        entityQuery,
        details,
        country,
        week,
        year,
        weeksQuery,
        row,
        rowQuery,
        series: row?.timeSeries,
        name: details?.name || null,
        isLoading:
            entityQuery.isLoading ||
            rowQuery.isLoading ||
            !(country && week && year),
        isEmpty: isEmptyRow({
            rowQuery,
            row,
            volumeKey: 'non_cum_avg_weekly_likes',
        }),
        coverage,
        coveredCountries: INSTAGRAM_COUNTRIES,
    });
};

/** The siblings the category score card compares this entity against. */
export const useInstagramSiblings = ({
    parentCategory,
    country,
    controller,
}) => {
    const query = useInstagramData({
        enabled: !!(parentCategory && country),
        countries: country ? [country.id] : null,
        categories: parentCategory ? parentCategory.children : null,
        paginateBy: 'categories',
        controller,
    });
    return {
        siblings: query.data,
        ready: !!(query.data && query.isSuccess),
    };
};

export default useInstagramEntity;
